import { Port, createEmptyPort } from "../FlightInformation/Port";
import { GoodsInfo } from "../GoodsInformation";
import {
    BaseDestinationInfo,
    BusinessDestinationInfo,
    CustomerDestinationInfo,
    MaritimeDestinationInfo,
    createDefaultBusinessDestinationInfo,
    createDefaultCustomerDestinationInfo,
    createDefaultMaritimeDestinationInfo
} from "./DestinationInfo";
import { OrderStatus } from "./OrderStatus";
import { PickupLocationInfo } from "./PickupLocationInfo";
import { ReceiveStatus } from "./ReceiveStatus";
import {
    BaseRequesterInfo,
    BusinessRequesterInfo,
    CustomerRequesterInfo,
    MaritimeRequesterInfo,
    createDefaultBusinessRequesterInfo,
    createDefaultCustomerRequesterInfo,
    createDefaultMaritimeRequesterInfo
} from "./RequesterInfo";

/**
 * 配送情報の種類
 */
export const OrderInfoType = {
    // 海上自衛隊
    MaritimeOrderInfo: "MaritimeOrderInfo",
    // BtoB
    BusinessOrderInfo: "BusinessOrderInfo",
    // BtoC
    CustomerOrderInfo: "CustomerOrderInfo",
} as const;
export type OrderInfoType = typeof OrderInfoType[keyof typeof OrderInfoType];

/**
 * 荷物画像情報
 */
export type PackageImage = {
    pathName: string
    imageBase64: string
}

/**
 * 配送情報
 */
export interface BaseOrderInfo<T extends BaseRequesterInfo, U extends BaseDestinationInfo> {
    /**
     * 注文番号
     */
    orderID: string,
    /**
     * 注文情報の種類
     */
    type: OrderInfoType,
    /**
     * ビジネスパートナーID
     */
    businessPartnerId: string,
    /**
     * 外部ID
     */
    externalOrderID?: string,
    /**
     * 注文日
     */
    orderDate: string,
    /**
     * 配送希望日時
     */
    preferredDeliveryDate: string,
    /**
     * 配送希望日時
     */
    preferredDeliveryTime: string,
    /**
     * 出発ポート
     */
    departure: Port,
    /**
     * 到着ポート
     */
    arrival: Port,
    /**
     * 配送物情報
     */
    goods: Array<GoodsInfo>,

    /**
     *　重心位置（東京都実証だけの対応）
     */
    positionCenterGravity?: {
        x: number,
        y: number,
        z: number
    },
    /**
     * カスタム重量
     * goodsに重量が設定されなかった場合
     */
    customWeight?: number,
    /**
     * 配送先情報
     */
    destination: U,
    /**
     * 配送依頼をした団体の情報
     */
    requester: T,
    /**
     * 配送依頼のステータス
     */
    status: OrderStatus,
    /**
     * 配送物の受け取り状態
     */
    receiveStatus: ReceiveStatus,
    /**
     * 備考
     */
    remarks: string,
    /**
     * 受け取り場所情報
     */
    pickupLocationInfo?: PickupLocationInfo,
    /**
     * 注文情報確認用のレシート画像のファイル名
     */
    receiptFileName?: string
}

/**
 * オーダーの状況
 */
export const TakeImagePhase = {
    /**
     * 荷物の受領時
     */
    Received: "received",
    /**
     * 荷物の梱包時
     */
    Packed: "packed",
    /**
     * 荷物の搭載時
     */
    Loaded: "loaded",
} as const;
export type TakeImagePhase = typeof TakeImagePhase[keyof typeof TakeImagePhase];

/**
 * 輸送経路の種類
 */
export const TransportRouteType = {
    // 民間空港経由
    ViaCivilianAirport: "民間空港経由",
    // 部内輸送
    InternalTransport: "部内輸送",
} as const;
export type TransportRouteType = typeof TransportRouteType[keyof typeof TransportRouteType];


export interface MaritimeOrderInfo extends BaseOrderInfo<MaritimeRequesterInfo, MaritimeDestinationInfo> {
    transportRoute: TransportRouteType,
    // 海上自衛隊が発行する伝票番号
    supplyHqVoucherNumber: string,
    // 日通等の民間企業が配送を行う場合の運送状番号
    carrierWaybillNumber: string
};

export interface BusinessOrderInfo extends
    BaseOrderInfo<BusinessRequesterInfo, BusinessDestinationInfo> { };

export interface CustomerOrderInfo extends
    BaseOrderInfo<CustomerRequesterInfo, CustomerDestinationInfo> { };

/**
 * 注文情報（Maritime/BtoB/BtoCのUnion）
 */
export type OrderInfo = MaritimeOrderInfo | BusinessOrderInfo | CustomerOrderInfo;

/**
 * 海上自衛隊向け配送情報初期データ作成
 */
export function createMaritimeOrderInfoData(): MaritimeOrderInfo {
    return {
        orderID: "",
        type: OrderInfoType.MaritimeOrderInfo,
        businessPartnerId: "",
        orderDate: "",
        preferredDeliveryDate: "",
        preferredDeliveryTime: "",
        remarks: "",
        status: OrderStatus.Accepting,
        goods: new Array<GoodsInfo>(),
        departure: createEmptyPort(),
        arrival: createEmptyPort(),
        receiveStatus: ReceiveStatus.NotReceived,
        requester: createDefaultMaritimeRequesterInfo(),
        destination: createDefaultMaritimeDestinationInfo(),
        transportRoute: TransportRouteType.ViaCivilianAirport,
        supplyHqVoucherNumber: "",
        carrierWaybillNumber: ""
    };
};

/** 
 * BtoB向け配送情報初期データ作成
 */
export function createBusinessOrderInfoData(): BusinessOrderInfo {
    return {
        orderID: "",
        type: OrderInfoType.BusinessOrderInfo,
        businessPartnerId: "",
        orderDate: "",
        preferredDeliveryDate: "",
        preferredDeliveryTime: "",
        remarks: "",
        status: OrderStatus.Accepting,
        goods: new Array<GoodsInfo>(),
        departure: createEmptyPort(),
        arrival: createEmptyPort(),
        receiveStatus: ReceiveStatus.NotReceived,
        requester: createDefaultBusinessRequesterInfo(),
        destination: createDefaultBusinessDestinationInfo(),
    };
};

/** 
 * BtoC向け配送情報初期データ作成
 */
export function createCustomerOrderInfoData(): CustomerOrderInfo {
    return {
        orderID: "",
        type: OrderInfoType.CustomerOrderInfo,
        businessPartnerId: "",
        orderDate: "",
        preferredDeliveryDate: "",
        preferredDeliveryTime: "",
        remarks: "",
        status: OrderStatus.Accepting,
        goods: new Array<GoodsInfo>(),
        departure: createEmptyPort(),
        arrival: createEmptyPort(),
        receiveStatus: ReceiveStatus.NotReceived,
        requester: createDefaultCustomerRequesterInfo(),
        destination: createDefaultCustomerDestinationInfo(),
    };
};

export interface OrderInfoContact {
    firstname: string;
    lastname: string;
    mailaddress?: string;
    tel?: string;
}